import { useEffect, useCallback } from 'react';

const useUtmLinkUpdater = () => {
    const persistUtmParams = useCallback(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const currentUtmData: Record<string, string> = {};

        for (const [key, value] of searchParams.entries()) {
            if (key.startsWith('utm_') && value) {
                currentUtmData[key] = value;
            }
        }

        if (Object.keys(currentUtmData).length === 0) {
            return;
        }

        try {
            const storedUtmData = JSON.parse(localStorage.getItem("utmData") || "{}");
            const mergedUtmData = { ...storedUtmData, ...currentUtmData };
            localStorage.setItem("utmData", JSON.stringify(mergedUtmData));
        } catch {
            localStorage.setItem("utmData", JSON.stringify(currentUtmData));
        }
    }, []);

    const handleLinkClick = useCallback((event: MouseEvent) => {
        const targetElement = event.target as Element | null;
        const linkElement = targetElement?.closest('a');

        if (!linkElement) {
            return;
        }

        // Don't process links that don't have href or are anchor links
        if (!linkElement.href || linkElement.href.startsWith('#')) {
            return;
        }

        // Don't process external links or mailto/tel links
        const linkUrl = new URL(linkElement.href);
        if (linkUrl.origin !== window.location.origin ||
            linkElement.href.startsWith('mailto:') ||
            linkElement.href.startsWith('tel:')) {
            return;
        }

        const currentPageParams = new URLSearchParams(window.location.search);
        const utmParamsToAppend = new Map();

        for (const [key, value] of currentPageParams.entries()) {
            if (key.startsWith('utm_')) {
                utmParamsToAppend.set(key, value);
            }
        }

        if (utmParamsToAppend.size === 0) {
            return;
        }

        let hasChanged = false;

        for (const [key, value] of utmParamsToAppend.entries()) {
            if (!linkUrl.searchParams.has(key)) {
                linkUrl.searchParams.append(key, value);
                hasChanged = true;
            }
        }

        if (hasChanged) {
            event.preventDefault();
            window.location.href = linkUrl.toString();
        }
    }, []);

    useEffect(() => {
        // Persist UTM values as soon as they are seen in URL
        persistUtmParams();
        const hasUtmParams = window.location.search.includes('utm_');

        if (hasUtmParams) {
            document.addEventListener('click', handleLinkClick);
        }

        return () => {
            document.removeEventListener('click', handleLinkClick);
        };
    }, [handleLinkClick, persistUtmParams]);
};

export default useUtmLinkUpdater;
